<template>
  <!-- GRID VIEW -->
  <div
    v-if="view === 'grid'"
    :class="['fm-card', { selected, 'drag-target': dragTarget, 'deleted-file': item.deleted_at, 'fm-reference': item.is_reference }]"
    :draggable="!item.is_reference"
    @click.exact="$emit('select-only', item.id)"
    @click.ctrl.exact="$emit('select', item.id)"
    @click.meta.exact="$emit('select', item.id)"
    @dblclick="$emit('open', item)"
    @contextmenu.prevent="$emit('context', { event: $event, item })"
    @dragstart="onDragStart"
    @dragover.prevent="$emit('drag-over-item', item.type === 'folder' ? item.id : null)"
    @dragleave="$emit('drag-over-item', null)"
    @drop.prevent="item.type === 'folder' && $emit('drop-on-item', item.id)"
  >
    <div class="fm-card-icon-wrap" :style="{ '--item-color': fileIcon.color }">
      <div class="fm-card-icon">{{ fileIcon.icon }}</div>
    </div>
    <div class="fm-card-name" :title="item.name">{{ item.name }}</div>
    <div v-if="isReference || associatedTasks.length" class="fm-tags">
      <span v-if="isReference" class="fm-source-tag">Biblioteca</span>
      <span v-for="task in associatedTasks" :key="task.id" class="fm-reference-tag">{{ task.label }}</span>
    </div>
    <div class="fm-card-meta">{{ item.type === 'folder' ? 'Carpeta' : item.ext + ' • ' + formatSize(item.size) }}</div>
  </div>

  <!-- LIST VIEW -->
  <tr
    v-else
    :class="['fm-row', { selected, 'drag-target': dragTarget, 'deleted-file': item.deleted_at, 'fm-reference': item.is_reference }]"
    :draggable="!item.is_reference"
    @click.exact="$emit('select-only', item.id)"
    @click.ctrl.exact="$emit('select', item.id)"
    @click.meta.exact="$emit('select', item.id)"
    @dblclick="$emit('open', item)"
    @contextmenu.prevent="$emit('context', { event: $event, item })"
    @dragstart="onDragStart"
    @dragover.prevent="$emit('drag-over-item', item.type === 'folder' ? item.id : null)"
    @dragleave="$emit('drag-over-item', null)"
    @drop.prevent="item.type === 'folder' && $emit('drop-on-item', item.id)"
  >
    <td class="fm-row-check">
      <input type="checkbox" :checked="selected" @click.stop @change="$emit('select', item.id)" />
    </td>
    <td class="fm-row-name" :style="showDeleted ? { maxWidth: '300px' } : {}">
      <span class="fm-row-icon" :style="{ color: fileIcon.color }">{{ fileIcon.icon }}</span>
      <span class="fm-row-label">{{ item.name }}</span>
      <span v-if="isReference || associatedTasks.length" class="fm-tags">
        <span v-if="isReference" class="fm-source-tag">Biblioteca</span>
        <span v-for="task in associatedTasks" :key="task.id" class="fm-reference-tag">{{ task.label }}</span>
      </span>
    </td>
    <td class="fm-row-meta">
      <span v-if="item.type !== 'folder'">{{ item.views_count || 0 }}</span>
    </td>
    <td class="fm-row-meta">{{ item.user?.name }}</td>
    <td class="fm-row-meta">{{ formatDate(item.updated_at) }}</td>
    <td class="fm-row-meta">{{ formatSize(item.size) }}</td>
    <td class="fm-row-meta">
      <span v-if="item.type === 'folder'" class="fm-badge fm-badge-folder">Carpeta</span>
      <span v-else class="fm-badge" :style="{ '--c': fileIcon.color }">{{ (item.ext || 'archivo').toUpperCase() }}</span>
    </td>
    <td v-if="showDeleted" class="fm-row-meta">
			{{ item.deleted_by?.name }}
			{{ item.deleted_at ? ' • ' + formatDate(item.deleted_at) : '' }}
		</td>
  </tr>
</template>

<script>
import { formatSize, formatDate, getFileIcon } from './dummyData'
export default {
  name: 'FMItemCard',
  props: {
    item: Object,
    selected: Boolean,
    dragTarget: Boolean,
    view: { type: String, default: 'grid' },
		showDeleted: Boolean
  },
  computed: {
    fileIcon() { return getFileIcon(this.item) },
    isReference() { return Boolean(this.item?.is_reference || this.item?.is_readonly) },
    associatedTasks() {
      if (Array.isArray(this.item?.associated_tasks)) return this.item.associated_tasks
      const legacyLabel = this.item?.reference_parent?.label
      return legacyLabel ? [{ id: 'legacy-reference-parent', label: legacyLabel }] : []
    }
  },
  methods: {
    formatSize, formatDate,
    onDragStart(e) {
      if (this.isReference) {
        e.preventDefault()
        return
      }
      e.dataTransfer.effectAllowed = 'move'
      this.$emit('drag-start', [this.item.uid])
    }
  }
}
</script>

<style scoped>
/* GRID CARD */
.fm-card {
  display: flex; flex-direction: column; align-items: center;
  padding: 16px 12px 12px;
  border-radius: var(--fm-radius);
  border: 1px solid transparent;
  background: var(--fm-surface);
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, transform 0.12s;
  user-select: none;
  gap: 8px;
}
.fm-card:hover { background: var(--fm-surface2); border-color: var(--fm-border); }
.fm-reference { cursor: default; }
.fm-card.selected { background: var(--fm-surface2); border-color: var(--fm-accent); box-shadow: 0 0 0 2px var(--fm-accent-glow); }
.fm-card.drag-target { border-color: var(--fm-accent); background: var(--fm-accent-glow); transform: scale(1.03); }
.fm-card-icon-wrap {
  width: 56px; height: 56px;
  background: color-mix(in srgb, var(--item-color) 12%, var(--fm-surface3));
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 26px;
}
.fm-card-icon { line-height: 1; }
.fm-card-name {
  font-size: 12px; font-weight: 500;
  text-align: center; color: var(--fm-text);
  max-width: 100%; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; width: 100%;
}
.fm-card-meta { font-size: 11px; color: var(--fm-text2); }
.fm-tags { display: flex; flex-wrap: wrap; justify-content: center; gap: 4px; }

/* LIST ROW */
.fm-row {
  border-bottom: 1px solid var(--fm-border);
  cursor: pointer;
  transition: background 0.1s;
  user-select: none;
}
.fm-row:hover { background: var(--fm-surface2); }
.fm-row.selected { background: var(--fm-surface2); }
.fm-row.selected td { border-color: transparent; }
.fm-row td { padding: 9px 12px; vertical-align: middle; }
.fm-row.drag-target { background: var(--fm-accent-glow); }
.fm-row.drag-target .fm-row-label { color: var(--fm-primary); }
.fm-row-check { width: 40px; }
.fm-row-name { display: flex !important; align-items: center; gap: 10px; min-width: 0; max-width: 400px; }
.fm-row-icon { font-size: 16px; flex-shrink: 0; }
.fm-row-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; font-weight: 500; color: var(--fm-text); }
.fm-row .fm-tags { justify-content: flex-start; display: inline-flex; margin-left: 4px; vertical-align: middle; }
.fm-row-meta { font-size: 12px; color: var(--fm-text2); white-space: nowrap; }
.fm-badge {
  display: inline-block;
  font-size: 10px; font-weight: 600; font-family: var(--fm-mono);
  letter-spacing: 0.05em;
  padding: 2px 6px; border-radius: 4px;
  background: color-mix(in srgb, var(--c, var(--fm-secondary)) 15%, transparent);
  color: var(--c, var(--fm-secondary));
  border: 1px solid color-mix(in srgb, var(--c, var(--fm-secondary)) 25%, transparent);
}
.fm-badge-folder { --c: #fbbf24; }
.deleted-file { opacity: 0.5; }
.fm-source-tag, .fm-reference-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 10px;
  line-height: 14px;
  white-space: nowrap;
}
.fm-source-tag { background: #eef2ff; color: #2d4b95; }
.fm-reference-tag { background: #fff4e5; color: #9a5b00; }
</style>